import { useCallback } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { Toast } from "@sscs/ui";
import { useCurrentOrganization, usePercentageSlider } from "Hooks";
import { MAX_PERCENTAGE, apiErrorToast, clampPercentageInput } from "Utils";
import { useCreateGroupMutation } from "../../services/organizations.api";
import {
  GroupFormData,
  GroupFormPercentageSlider,
  GroupFormReturn,
} from "../../types";
import { generateGroupFormSchema, getCreateGroupFormBody } from "../../utils";

type UseAddGroupFormProps = {
  filestreamPercentageFieldName: keyof GroupFormPercentageSlider;
  projectsPercentageFieldName: keyof GroupFormPercentageSlider;
  onClose: () => void;
  totalPortalCapacity?: number;
};

const useAddGroupForm = ({
  filestreamPercentageFieldName,
  projectsPercentageFieldName,
  onClose,
  totalPortalCapacity,
}: UseAddGroupFormProps): GroupFormReturn => {
  const currentOrganization = useCurrentOrganization();
  const [createGroup] = useCreateGroupMutation();

  const {
    schema: percentageSliderSchema,
    formDefaultValues,
    getSliderPercentages,
    onChangePercentage,
  } = usePercentageSlider<GroupFormPercentageSlider>({
    firstPercentageName: filestreamPercentageFieldName,
    secondPercentageName: projectsPercentageFieldName,
    defaultSliderValue: 50,
  });

  const addGroupSchema = generateGroupFormSchema(
    percentageSliderSchema,
    totalPortalCapacity,
  );

  const defaultValues = {
    ...formDefaultValues,
    name: "",
    description: "",
    is_default: false,
    oidc_sso_group_mappings: [],
    limited: 0,
  };

  const formMethods = useForm<GroupFormData>({
    resolver: yupResolver(addGroupSchema),
    defaultValues,
  });

  const {
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = formMethods;

  const onSubmit: SubmitHandler<GroupFormData> = async (data) => {
    try {
      await createGroup({
        body: getCreateGroupFormBody(data, currentOrganization?.id ?? ""),
      }).unwrap();

      Toast.show({
        title: "Group created",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not create group");
    }

    onClose();
  };

  const filestream_percentage = watch(filestreamPercentageFieldName);
  const {
    firstPercentage: filestreamPercentage,
    secondPercentage: projectsPercentage,
  } = getSliderPercentages(filestream_percentage);

  const onPercentageSliderChange = useCallback(
    (value: number) => {
      onChangePercentage(value, setValue);
    },
    [onChangePercentage, setValue],
  );

  const onProjectsPercentageInputChange = useCallback(
    (projectsPercentageInputValue: string): void => {
      const clampedProjects = clampPercentageInput(
        projectsPercentageInputValue,
      );

      setValue(projectsPercentageFieldName, clampedProjects);
      setValue(
        filestreamPercentageFieldName,
        clampedProjects === null ? null : MAX_PERCENTAGE - clampedProjects,
      );
    },
    [setValue, filestreamPercentageFieldName, projectsPercentageFieldName],
  );

  return {
    onSubmit: handleSubmit(onSubmit),
    errors,
    formMethods: formMethods,
    filestreamPercentage,
    projectsPercentage,
    onPercentageSliderChange,
    onProjectsPercentageInputChange,
  };
};

export default useAddGroupForm;
